
import React, { createContext, useContext, useState } from 'react'

interface DateRangeOutput {
  startDate: string
  endDate: string
}

interface ShowOptions {
  date?: string
  startDate?: string
  endDate?: string
  projectStartDate?: string
  projectEndDate?: string
  nonWorkingDays: string[]
  onChange?: (date: string) => void
  onChangeRange?: (range: DateRangeOutput) => void
}

interface DatePickerContextType {
  show: boolean
  mode: 'day' | 'range'
  date?: string
  startDate?: string
  endDate?: string
  projectStartDate?: string
  projectEndDate?: string
  nonWorkingDays: string[]
  showDatePicker: (options: ShowOptions) => void
  showDateRangePicker: (options: ShowOptions) => void
  onChange: (date: string) => void
  onChangeRange: (range: DateRangeOutput) => void
  hideDatePicker: () => void
}

const DatePickerContext = createContext<DatePickerContextType | undefined>(undefined)

export const DatePickerProvider = (props: { children: React.ReactNode }) => {
  const [show, setShow] = useState(false)
  const [mode, setMode ] = useState<'day' | 'range'>('day')
  const [date, setDate] = useState<string | undefined>('')
  const [startDate, setStartDate] = useState<string | undefined>('')
  const [endDate, setEndDate] = useState<string | undefined>('')
  const [projectStartDate, setProjectStartDate] = useState<string | undefined>(undefined)
  const [projectEndDate, setProjectEndDate] = useState<string | undefined>(undefined)
  const [nonWorkingDays, setNonWorkingDays] = useState<string[]>([])
  const [onChange, setOnChange] = useState<any>()
  const [onChangeRange, setOnChangeRange] = useState<any>()

  const showDatePicker = (options: ShowOptions) => {

    const {
      date,
      projectStartDate,
      projectEndDate,
      nonWorkingDays,
      onChange,
    } = options

    setDate(date)
    setMode('day')
    setProjectStartDate(projectStartDate || undefined)
    setProjectEndDate(projectEndDate || undefined)
    setNonWorkingDays(nonWorkingDays)
    setOnChange(() => onChange)
    setShow(true)
  }

  const showDateRangePicker = (options: ShowOptions) => {

    const {
      startDate,
      endDate,
      projectStartDate,
      projectEndDate,
      nonWorkingDays,
      onChangeRange
    } = options

    setStartDate(startDate)
    setEndDate(endDate)
    setMode('range')
    setProjectStartDate(projectStartDate || undefined)
    setProjectEndDate(projectEndDate || undefined)
    setNonWorkingDays(nonWorkingDays)
    setOnChangeRange(() => onChangeRange)
    setShow(true)
  }

  const hideDatePicker = () => {

    setShow(false)
  }

  const value = {
    show,
    date,
    mode,
    startDate,
    endDate,
    projectStartDate,
    projectEndDate,
    nonWorkingDays,
    showDatePicker,
    showDateRangePicker,
    onChange,
    onChangeRange,
    hideDatePicker,
  }

  return (
    <DatePickerContext.Provider value={value}>
      {props.children}
    </DatePickerContext.Provider>
  )
}


export const useDatePicker = () => {

  const context = React.useContext(DatePickerContext)

  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider')
  }

  return context
}
